import React, { createContext, useState, useContext, useEffect } from 'react'
import { apiClient } from '../api';
import Loader from '../components/Loader';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [values, setValues] = useState({
        apiKey: null,
        user: null,
        citationStyles: [],
        selectedCitationStyle: 'ama',
        projects: [],
        selectedProject: null,
    });


    useEffect(() => {
        getDefaultValues();
    }, []);

    // Don't auto-authenticate on startup - let user explicitly login
    // This prevents hanging on "Loading..." with stale/invalid saved keys

    async function fetchUserData() {
        if (!values.apiKey) return;

        setLoading(true);
        try {
            // Discover and set the correct environment for this API key
            const baseURL = await apiClient.authenticateWithDiscovery(values.apiKey);
            apiClient.setBaseURL(baseURL);
            apiClient.setApiKey(values.apiKey);

            // Now fetch user data from the discovered environment
            const data = await apiClient.me();

            if (data) {
                setAuthData({ ...data, citationStyles: data["citation_styles"] });
            } else {
                throw new Error("Invalid credentials");
            }
        } catch (err) {
            // Clear any stale cached data on auth failure
            apiClient.clearCachedBaseURL();
            // Don't re-throw - let the app render the login form instead
            // The LoginForm will handle errors from its own authentication attempt
        } finally {
            setLoading(false);
        }
    }

    async function getDefaultValues() {
        Office.onReady(async () => {
            try {
                // Don't load saved apiKey on startup - user must login explicitly
                // This prevents slow discovery process from blocking the UI
                const citationStyle = Office.context.document.settings.get("citationStyle") || "";
                setValues((v) => ({ ...v, selectedCitationStyle: citationStyle }));

                // Load cached base URL and set it on apiClient
                // This ensures API calls work after page reload without re-authentication
                const cachedBaseURL = apiClient.getCachedBaseURL();
                if (cachedBaseURL) {
                    apiClient.setBaseURL(cachedBaseURL);
                }
            } catch (error) {
                // ignore
            } finally {
                setLoading(false);
            }
        });
    };

    const setAuthData = (data) => {
        setValues((prev) => ({
            ...prev,
            ...data,
        }));

        if (data.apiKey) {
            Office.context.document.settings.set("apiKey", data.apiKey || "");
            Office.context.document.settings.saveAsync(() => {});
        } else if (data.selectedCitationStyle) {
            Office.context.document.settings.set("citationStyle", data.selectedCitationStyle || "");
            Office.context.document.settings.saveAsync(() => {});
        }
    };

    const logout = () => {
        // Clear cached base URL on logout
        apiClient.clearCachedBaseURL();

        setValues({
            user: null,
            apiKey: null,
            citationStyles: [],
            selectedCitationStyle: '',
            projects: [],
            selectedProject: '',
        });
    };

    return (
        <AuthContext.Provider value={{ ...values, setAuthData, logout }}>
            <Loader loading={loading} />
            {children}
        </AuthContext.Provider>
    )
};

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) throw new Error('useAuth must be used within AuthProvider')
    return context
};
